import { localStorageKeys } from './constants';

const PBKDF2_ITERATIONS = 100_000;
const AES_IV_LENGTH = 12;

function getPbkdf2Salt() {
  return 'reg-branch-cache-pbkdf2-salt-v1-set-REACT_APP_REG_BRANCH_CACHE_PBKDF2_SALT_shfbjdiuhsdfdhfjb453';
}

function bytesToBase64(bytes) {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function deriveAesKeyFromToken(accessToken) {
  const enc = new TextEncoder();
  const salt = enc.encode(getPbkdf2Salt());
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(accessToken),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypts the OnLoad API envelope (axios `response.data`) for offline-first UX.
 * Security note: anyone who can run JS as this origin with the same token can decrypt.
 * This mainly protects casual localStorage inspection; use a strong REACT_APP_REG_BRANCH_CACHE_PBKDF2_SALT in production.
 */
export async function encryptRegBranchOnLoadEnvelope(accessToken, apiEnvelope) {
  const enc = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(AES_IV_LENGTH));
  const key = await deriveAesKeyFromToken(accessToken);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(JSON.stringify(apiEnvelope))
  );
  return JSON.stringify({
    v: 1,
    iv: bytesToBase64(iv),
    ct: bytesToBase64(new Uint8Array(ciphertext)),
  });
}

export async function decryptRegBranchOnLoadEnvelope(accessToken, storedJson) {
  if (!storedJson || typeof storedJson !== 'string') return null;
  let parsed;
  try {
    parsed = JSON.parse(storedJson);
  } catch {
    return null;
  }
  if (parsed.v !== 1 || !parsed.iv || !parsed.ct) return null;
  try {
    const iv = base64ToBytes(parsed.iv);
    const ct = base64ToBytes(parsed.ct);
    const key = await deriveAesKeyFromToken(accessToken);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
    const text = new TextDecoder().decode(decrypted);
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Reads `RegionalBranchInfo` default managing branch from OnLoadRegBranchInformation envelope.
 * @param {object} apiEnvelope — axios `response.data` (`{ success, data: { RegionalBranchInfo, ... } }`)
 */
export function extractDefaultManagingBranchName(apiEnvelope) {
  const inner = apiEnvelope?.data;
  if (!inner || typeof inner !== 'object') return null;
  const info = inner.RegionalBranchInfo ?? inner.regionalBranchInfo;
  if (!info || typeof info !== 'object') return null;
  const raw =
    info.defaultManagingBranchName ??
    info.defaultManagingBranch ??
    info.default_managing_branch_name ??
    info.default_managing_branch ??
    info.managingBranchName ??
    info.managing_branch_name ??
    info.branchName ??
    info.branch_name;
  if (raw == null || raw === '') return null;
  return String(raw).trim().toLowerCase();
}

/** Sync read of plaintext default managing branch stored on `regBranchOnLoadCacheV1`. */
export function readDefaultManagingBranchFromOnLoadCacheSync() {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(localStorageKeys.REG_BRANCH_ONLOAD_CACHE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const plain = parsed.defaultManagingBranch ?? parsed.defaultManagingBranchName;
    if (plain == null || String(plain).trim() === '') return null;
    return String(plain).trim().toLowerCase();
  } catch {
    return null;
  }
}

/** Backfills plaintext managing branch on existing encrypted cache blobs (migration). */
export function upsertDefaultManagingBranchOnCacheBlob(managingBranch) {
  if (!managingBranch || typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(localStorageKeys.REG_BRANCH_ONLOAD_CACHE);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed.v !== 1 || !parsed.iv || !parsed.ct) return;
    const normalized = String(managingBranch).trim().toLowerCase();
    if (parsed.defaultManagingBranch === normalized) return;
    parsed.defaultManagingBranch = normalized;
    localStorage.setItem(localStorageKeys.REG_BRANCH_ONLOAD_CACHE, JSON.stringify(parsed));
  } catch {
    /* ignore */
  }
}

export async function saveRegBranchOnLoadCache(accessToken, apiEnvelope) {
  if (!accessToken || !apiEnvelope || typeof window === 'undefined') return;
  try {
    const blob = await encryptRegBranchOnLoadEnvelope(accessToken, apiEnvelope);
    const managingBranch = extractDefaultManagingBranchName(apiEnvelope);
    if (managingBranch) {
      const stored = JSON.parse(blob);
      stored.defaultManagingBranch = managingBranch;
      localStorage.setItem(localStorageKeys.REG_BRANCH_ONLOAD_CACHE, JSON.stringify(stored));
    } else {
      localStorage.setItem(localStorageKeys.REG_BRANCH_ONLOAD_CACHE, blob);
    }
  } catch (e) {
    console.warn('saveRegBranchOnLoadCache failed', e);
  }
}

export async function readRegBranchOnLoadCache(accessToken) {
  if (!accessToken || typeof window === 'undefined') return null;
  const raw = localStorage.getItem(localStorageKeys.REG_BRANCH_ONLOAD_CACHE);
  if (!raw) return null;
  return decryptRegBranchOnLoadEnvelope(accessToken, raw);
}

export function clearRegBranchOnLoadCache() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(localStorageKeys.REG_BRANCH_ONLOAD_CACHE);
  } catch {
    /* ignore */
  }
}
